/**
 * `<McpDevicesTable>` — the "Active devices" section of the MCP Integration
 * card (U4 split out of McpIntegration.tsx; behavior unchanged).
 *
 * Universal (desktop + web): every minted token with its last-used timestamp,
 * plus per-row revoke — so a user can cut off a lost laptop's token from any
 * signed-in browser. Owns the list + revoke state machines; the branded
 * confirm dialog is injected by the wrapper (which renders the modal element
 * at the card's original DOM position).
 */

import { useCallback, useEffect, useState } from 'react';
import type { ConfirmModalProps } from '@/components';
import { callEndpoint } from '@/api';

interface ListedDevice {
  id: string;
  label: string;
  hash_prefix: string;
  created_at: string;
  last_used_at: string | null;
  // Not returned by /list (it filters revoked rows out by default), but kept
  // optional so the type doesn't break if the backend ever inlines it.
  revoked_at?: string | null;
}

interface ListResponse {
  tokens: ListedDevice[];
}

function fmtTimestamp(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

interface McpDevicesTableProps {
  /** Bump to refetch the list (the wrapper bumps it after a successful mint). */
  refreshSignal: number;
  /** `useConfirmModal().confirm` from the wrapper (facelift-v3 Phase 6:
   *  branded confirm replaces window.confirm). */
  confirm: (opts: Omit<ConfirmModalProps, 'onConfirm' | 'onCancel'>) => Promise<boolean>;
}

export function McpDevicesTable({ refreshSignal, confirm }: McpDevicesTableProps): JSX.Element {
  const [devices, setDevices] = useState<ListedDevice[] | null>(null);
  const [devicesError, setDevicesError] = useState<string | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  // Fetch the devices list on mount + whenever the wrapper signals a mint.
  const refreshDevices = useCallback(async () => {
    try {
      const resp = await callEndpoint<ListResponse>('GET', 'system/mcp_token/list');
      setDevices(resp.tokens ?? []);
      setDevicesError(null);
    } catch (err) {
      setDevicesError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  useEffect(() => {
    void refreshDevices();
  }, [refreshDevices, refreshSignal]);

  const onRevokeRemote = useCallback(
    async (id: string) => {
      const ok = await confirm({
        eyebrow: 'MCP',
        title: 'Revoke device token',
        message:
          'Revoke this device token? The MCP client using it will be cut off immediately.',
        destructive: true,
        confirmLabel: 'Revoke',
      });
      if (!ok) return;
      setRevokingId(id);
      try {
        await callEndpoint<unknown>('POST', `system/mcp_token/${id}/revoke`);
        void refreshDevices();
      } catch (err) {
        setDevicesError(err instanceof Error ? err.message : String(err));
      } finally {
        setRevokingId(null);
      }
    },
    [refreshDevices, confirm],
  );

  return (
    <div className="mcp-section">
      <h4>Active devices</h4>
      <p className="mcp-hint">
        Every minted token, with its last-used timestamp. Revoke from here if a device is lost or
        a token leaks — the next request from that token 401s immediately.
      </p>
      {devicesError !== null && <div className="mcp-error">Could not load: {devicesError}</div>}
      {devices === null ? (
        <div className="mcp-hint">Loading…</div>
      ) : devices.length === 0 ? (
        <div className="mcp-hint">No active tokens yet.</div>
      ) : (
        <table className="mcp-devices">
          <thead>
            <tr>
              <th>Label</th>
              <th>Hash</th>
              <th>Created</th>
              <th>Last used</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {devices.map((d) => (
              <tr key={d.id}>
                <td>{d.label}</td>
                <td>
                  <code>{d.hash_prefix}…</code>
                </td>
                <td>{fmtTimestamp(d.created_at)}</td>
                <td>{fmtTimestamp(d.last_used_at)}</td>
                <td className="settings-cell-end">
                  <button
                    type="button"
                    className="btn btn--sm"
                    onClick={() => void onRevokeRemote(d.id)}
                    disabled={revokingId === d.id}
                  >
                    {revokingId === d.id ? 'Revoking…' : 'Revoke'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
